export const MEMBERSHIP_HANDLING_MODES = Object.freeze({
  HOSTED_STRIPE: 'hosted_stripe',
  HYBRID: 'hybrid',
  CLUB_GATED: 'club_gated',
  CLUB_PAY_LATER: 'club_pay_later',
});

const VALID_MODES = new Set(Object.values(MEMBERSHIP_HANDLING_MODES));
const EXTERNAL_PAYMENT_DISPLAY_MODES = new Set(['price_and_instructions', 'price_only', 'instructions_only']);

export function normalizeMembershipHandlingMode(value) {
  const mode = String(value || '').trim().toLowerCase();
  return VALID_MODES.has(mode) ? mode : MEMBERSHIP_HANDLING_MODES.HOSTED_STRIPE;
}

export function getMembershipHandling(team = {}) {
  const source = team?.membershipHandling || {};
  return {
    mode: normalizeMembershipHandlingMode(source.mode),
    payLaterRequiresRoundAssignment: source.payLaterRequiresRoundAssignment === true,
    scheduledTransition: source.scheduledTransition || null,
  };
}

export function isClubManagedMembershipMode(value) {
  const mode = normalizeMembershipHandlingMode(value);
  return mode === MEMBERSHIP_HANDLING_MODES.CLUB_GATED
    || mode === MEMBERSHIP_HANDLING_MODES.CLUB_PAY_LATER;
}

export function supportsHostedMembershipPayments(value) {
  const mode = normalizeMembershipHandlingMode(value);
  return mode === MEMBERSHIP_HANDLING_MODES.HOSTED_STRIPE
    || mode === MEMBERSHIP_HANDLING_MODES.HYBRID;
}

export function supportsOfflineMembershipPayments(value) {
  const mode = normalizeMembershipHandlingMode(value);
  return mode === MEMBERSHIP_HANDLING_MODES.HYBRID
    || isClubManagedMembershipMode(mode);
}

export function usesAnnualClubMembershipBilling(value) {
  return supportsOfflineMembershipPayments(value);
}

export function isExternalPaymentSetupReady(setup = {}) {
  const displayMode = typeof setup.externalPaymentDisplayMode === 'string'
    ? setup.externalPaymentDisplayMode.trim()
    : '';
  if (!EXTERNAL_PAYMENT_DISPLAY_MODES.has(displayMode)) return false;
  const instructions = typeof setup.externalPaymentInstructions === 'string'
    ? setup.externalPaymentInstructions.trim()
    : '';
  if (instructions.length > 1000 || (displayMode !== 'price_only' && !instructions)) return false;
  if (displayMode === 'instructions_only') return true;
  const memberChoice = setup.cadenceMode === 'member_choice';
  const fixedMonthly = setup?.fixedInterval?.unit === 'month';
  const positivePrice = (row = {}) => memberChoice
    ? Number(row.baseRate || 0) > 0 && Number(row.baseRateYearly || 0) > 0
    : Number(fixedMonthly ? row.baseRate : row.baseRateYearly) > 0;
  if (!positivePrice(setup)) return false;
  if (memberChoice && (Number(setup?.memberChoice?.monthly?.discountPct || 0) >= 100
    || Number(setup?.memberChoice?.yearly?.discountPct || 0) >= 100)) return false;
  if ([...Object.values(setup.tagOverrides || {}), ...Object.values(setup.tagOverridesYearly || {})]
    .some((value) => Number(value) <= 0)) return false;
  return (Array.isArray(setup.familyTiers) ? setup.familyTiers : []).every(positivePrice);
}
